import { useAuth0 } from '@auth0/auth0-react'
import { CaptureContext, captureException, captureMessage } from '@sentry/core'
import { useRuntimeVariables } from 'app-kit'
import rawAxios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { AuthInactivityMonitor } from 'features/auth/AuthInactivityMonitor'
import { doesHaveRequiredPermissionsBase } from 'features/auth/doesHaveRequiredPermissionsBase'
import {
  GeckoAuthContext,
  GeckoLogoutParams,
} from 'features/auth/geckoAuthContext'
import { RolodexPermissions } from 'features/auth/permissions/rolodexPermissionsList'
import { useRolodexPermissionsUserMetadata } from 'features/auth/permissions/useRolodexPermissionsUserMetadata'
import { jwtDecode } from 'jwt-decode'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { computePermissions } from './helper'

/**
 * A component which handles Auth0 authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const Auth0AuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const auth0 = useAuth0()
  const { AUTH0_AUDIENCE, ROLODEX_URL, AUTH0_CLIENT_ID } = useRuntimeVariables()
  const [authorization, setAuthorization] = useState<string>()
  const [validRefreshToken, setValidRefreshToken] = useState<boolean>(true)
  const [tokenExpiration, setTokenExpiration] = useState<Date>()

  const attemptGetAccessToken = React.useCallback(() => {
    auth0
      .getAccessTokenSilently({
        authorizationParams: { audience: AUTH0_AUDIENCE },
      })
      .then((token) => {
        const parsedJwt = jwtDecode(token)
        if (!parsedJwt.exp) {
          throw new Error('Auth0 jwt contains no expiration date')
        }
        const parsedExpirationDate = new Date(parsedJwt.exp * 1000)
        setTokenExpiration(parsedExpirationDate)
        setAuthorization(`Bearer ${token}`)
        setValidRefreshToken(true)

        captureMessage('Token retrieved from Auth0', {
          tags: { host: window.location.origin, exp: parsedJwt.exp },
        })
      })
      .catch((err) => {
        const cachedRefreshToken = localStorage.getItem(
          `@@auth0spajs@@::${AUTH0_CLIENT_ID}::${AUTH0_AUDIENCE}::openid profile email offline_access`
        )
        const captureContext: CaptureContext = { tags: {} }
        if (cachedRefreshToken) {
          const cachedTokenJson = JSON.parse(cachedRefreshToken)
          captureContext.tags!.refreshTokenExp = cachedTokenJson.expiresAt
        }

        captureException(err, captureContext)
        setValidRefreshToken(false)
      })
  }, [auth0, AUTH0_AUDIENCE, AUTH0_CLIENT_ID])

  // only get the access token here on the first render
  // all subsequent token retrievals should be handled by the inactivity monitor
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(attemptGetAccessToken, [])

  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    newAxios.interceptors.request.use((requestConfig: AxiosRequestConfig) => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: authorization ?? null,
        } as AxiosRequestHeaders,
      }
    })
    return newAxios
  }, [authorization])

  const { data: currentUserMetadata, mutate: refreshCurrentUser } =
    useRolodexPermissionsUserMetadata(
      authorizedAxios,
      authorization,
      ROLODEX_URL
    )
  const [showPermissionGates, setShowPermissionGates] = useState<boolean>(false)

  useEffect(() => {
    if (!currentUserMetadata?.roles) return
  }, [currentUserMetadata])

  const { permissions } = useMemo(() => {
    // Need to wait for the mask to be set so we don't return inaccurate permissions
    if (!currentUserMetadata) {
      return { permissions: undefined }
    }
    return computePermissions(currentUserMetadata)
  }, [currentUserMetadata])

  const hasPermissions = useCallback(
    (toCheck: RolodexPermissions[]) =>
      doesHaveRequiredPermissionsBase(permissions, toCheck),
    [permissions]
  )

  const logout = useCallback(
    (geckoLogoutParams: GeckoLogoutParams) => {
      auth0.logout({ logoutParams: { returnTo: geckoLogoutParams.returnTo } })
    },
    [auth0]
  )

  return (
    <>
      <AuthInactivityMonitor
        tokenExpiration={tokenExpiration}
        validRefreshToken={validRefreshToken}
        attemptGetAccessToken={attemptGetAccessToken}
        redirectToLogin={() => {
          auth0.loginWithRedirect({
            appState: { returnTo: location.pathname ?? window.location.href },
          })
        }}
      />
      <GeckoAuthContext.Provider
        value={{
          // Auth
          authorizedAxios,
          authorization,
          // User
          currentUser: currentUserMetadata,
          refreshCurrentUser,
          // Permissions
          permissions,
          hasPermissions,
          isAuthenticated: !!authorization,
          // Permissions V2
          user: {
            email: auth0.user?.email ?? '',
            name: auth0.user?.name ?? '',
            familyName: auth0.user?.family_name ?? '',
            givenName: auth0.user?.given_name ?? '',
            picture: auth0.user?.picture ?? '',
          },
          logout: logout,
          showPermissionGates: showPermissionGates,
          setShowPermissionGates: setShowPermissionGates,
        }}>
        {children}
      </GeckoAuthContext.Provider>
    </>
  )
}
